import * as React from "react";

import { v4 as uuidv4 } from "uuid";

import { trackEvent } from "@needl-ai/common/analytics";

import { useChatActions } from "./ChatActions";
import { ChatContext } from "./ChatContextTypes";
import { useChatState } from "./ChatState";
import { useFileUploadContext } from "./FileUploadContext";

import type { ChatContextType } from "./ChatContextTypes";
import type { Message } from "@/types/chat";

import { useAskNeedlUpload } from "@/hooks/useAskNeedlUpload";
import { useAsyncSessionTracker } from "@/hooks/useAsyncSessionTracker";
import {
  useCreateUpdateSession,
  useInvalidateSession,
} from "@/hooks/useCreateUpdateSession";
import { useFetchAskNeedlTools } from "@/hooks/useFetchAskNeedlTools";
import { useRMChat } from "@/hooks/useRMChat";
import { type AskNeedlToolGroup, type AskNeedlTools } from "@/types/api";

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { state, setters } = useChatState();
  const { setMessages: setMessagesState } = setters;

  const { state: fileUploadState } = useFileUploadContext();
  const { clearAttachmentsOnly } = useAskNeedlUpload();

  const { data: toolsData } = useFetchAskNeedlTools();

  const pendingRequestIdRef = React.useRef<string | null>(null);
  const invalidateSession = useInvalidateSession();

  const {
    isLoading,
    isStreaming,
    isError,
    streamingAnswer,
    toolCallLogs,
    citationGenerationStarted,
    data,
    askRMChat,
    clearEventData,
    setLoading,
    aiMessageId,
  } = useRMChat();

  React.useEffect(() => {
    if (!aiMessageId) return;
    pendingRequestIdRef.current = aiMessageId;
    invalidateSession(state.sessionId);
  }, [aiMessageId, invalidateSession, state.sessionId]);

  const createUpdateSessionMutation = useCreateUpdateSession();

  const voiceCountRef = React.useRef(0);

  const incrementVoiceCount = React.useCallback(() => {
    voiceCountRef.current += 1;
  }, []);

  const { trackSession, asyncStatuses } = useAsyncSessionTracker(
    state.sessionId,
    state.messages,
    isLoading || isStreaming
  );

  const mergeToolsWithSessionData = React.useCallback(
    (apiTools: AskNeedlTools, sessionTools?: AskNeedlTools): AskNeedlTools => {
      if (!sessionTools) {
        return apiTools;
      }

      const mergedTools: AskNeedlTools = apiTools.map((apiTool) => {
        if (apiTool.type === "toggle") {
          const sessionTool = sessionTools.find(
            (st) => st.id === apiTool.id && st.type === "toggle"
          );
          return {
            ...apiTool,
            enabled:
              sessionTool && sessionTool.type === "toggle"
                ? sessionTool.enabled
                : apiTool.enabled,
          };
        } else if (apiTool.type === "group") {
          const sessionTool = sessionTools.find(
            (st) => st.id === apiTool.id && st.type === "group"
          );

          if (sessionTool && sessionTool.type === "group") {
            const sessionGroup = sessionTool as AskNeedlToolGroup;
            const apiGroup = apiTool as AskNeedlToolGroup;

            return {
              ...apiGroup,
              sources: apiGroup.sources.map((apiSource) => {
                const sessionSource = sessionGroup.sources.find(
                  (ss) => ss.id === apiSource.id
                );
                return {
                  ...apiSource,
                  enabled: sessionSource?.enabled ?? apiSource.enabled,
                };
              }),
            };
          }

          return apiTool;
        }

        return apiTool;
      });

      return mergedTools;
    },
    []
  );

  const getFeedId = React.useCallback(() => {
    return fileUploadState.attachment_feed_id;
  }, [fileUploadState.attachment_feed_id]);

  const getAttachments = React.useCallback(() => {
    return fileUploadState.attachments;
  }, [fileUploadState.attachments]);

  const saveSession = React.useCallback(
    async (
      messages: Message[],
      options?: {
        skipSessionInvalidation?: boolean;
        schedulingMessageDisabled?: boolean;
      }
    ) => {
      if (state.sessionId && messages.length > 0) {
        await createUpdateSessionMutation.mutateAsync({
          sessionId: state.sessionId,
          sessionState: {
            messages,
            tools: state.tools || [],
            attachment_feed_id: fileUploadState.attachment_feed_id,
            report_type: state.reportType || undefined,
            is_artifact_report_scheduling_message_disabled:
              options?.schedulingMessageDisabled ??
              state.isArtifactReportSchedulingMessageDisabled,
          },
          title: state.sessionTitle || undefined,
          skipSessionInvalidation: options?.skipSessionInvalidation,
        });
      }
    },
    [
      state.sessionId,
      state.tools,
      state.reportType,
      state.sessionTitle,
      state.isArtifactReportSchedulingMessageDisabled,
      fileUploadState.attachment_feed_id,
      createUpdateSessionMutation,
    ]
  );

  const isSessionBusy = isLoading || isStreaming;

  const disableArtifactReportSchedulingMessage = React.useCallback(() => {
    if (isSessionBusy || pendingRequestIdRef.current) return;
    setters.setIsArtifactReportSchedulingMessageDisabled(true);
    void saveSession(state.messages, { schedulingMessageDisabled: true });
  }, [isSessionBusy, setters, saveSession, state.messages]);

  const markReportScheduled = React.useCallback(
    (reportId: string) => {
      if (isSessionBusy || pendingRequestIdRef.current) return;
      const nextMessages = state.messages.map((message) =>
        message.report_generation?.report_id === reportId
          ? {
              ...message,
              report_generation: {
                ...message.report_generation,
                is_scheduled: true,
              },
            }
          : message
      );
      setters.setMessages(nextMessages);
      void saveSession(nextMessages);
    },
    [isSessionBusy, state.messages, setters, saveSession]
  );

  // Initialize actions
  const actions = useChatActions({
    state,
    setters,
    rmChat: {
      askRMChat,
      clearEventData,
    },
    getFeedId,
    getAttachments,
    clearAttachmentsOnly,
    saveSession,
    setLoading,
    voiceCountRef,
  });

  const errorContextRef = React.useRef({
    streamingAnswer,
    aiMessageId,
    toolCallLogs,
    messages: state.messages,
  });
  errorContextRef.current = {
    streamingAnswer,
    aiMessageId,
    toolCallLogs,
    messages: state.messages,
  };

  React.useEffect(() => {
    if (!(data || isError) || !state.sessionId) return;

    if (isError) {
      trackEvent("response_error_shown", {
        session_id: state.sessionId,
        error_type: "sse_error",
      });

      const ctx = errorContextRef.current;
      const errorText =
        ctx.streamingAnswer || "Something went wrong. Please try again.";

      const errorBotMessage: Message = {
        id: ctx.aiMessageId || uuidv4(),
        text: errorText,
        sender: "ai",
        timestamp: new Date(),
        message_status: "NO_ANSWER_GENERATED",
        toolLogs:
          ctx.toolCallLogs.length > 0 ? [...ctx.toolCallLogs] : undefined,
      };

      setMessagesState([...ctx.messages, errorBotMessage]);
      pendingRequestIdRef.current = null;
      clearEventData();
      invalidateSession(state.sessionId);

      return;
    }

    // Backend owns session save for all session types.
    // Invalidate session so useSessionManagement can sync messages from DB.
    pendingRequestIdRef.current = null;
    invalidateSession(state.sessionId).then(() => clearEventData());
  }, [
    data,
    isError,
    state.sessionId,
    clearEventData,
    invalidateSession,
    setMessagesState,
  ]);

  const initializeToolsFromSession = React.useCallback(
    (sessionTools?: AskNeedlTools) => {
      if (toolsData) {
        const mergedTools = mergeToolsWithSessionData(toolsData, sessionTools);
        setters.setTools(mergedTools);
      }
    },
    [toolsData, mergeToolsWithSessionData, setters]
  );

  const initializeChatState = React.useCallback(() => {
    if (pendingRequestIdRef.current) {
      trackSession({
        requestId: pendingRequestIdRef.current,
        sessionId: state.sessionId,
        title:
          state.sessionTitle ||
          (state.messages[0]?.text as string) ||
          "Untitled",
        sessionPath: state.reportType ? "/report-automation" : "/c",
      });
    }
    pendingRequestIdRef.current = null;
    clearEventData();
    setters.setInput("");
    setters.setMessages([]);
    setters.setSessionId(uuidv4());
    setters.setTools(null);
    setters.setReportType(null);
    setters.setSessionTitle(null);
    setters.setIsArtifactReportSchedulingMessageDisabled(false);
  }, [
    setters,
    clearEventData,
    trackSession,
    state.sessionId,
    state.sessionTitle,
    state.messages,
    state.reportType,
  ]);

  const contextValue: ChatContextType = {
    sessionId: state.sessionId,
    messages: state.messages,
    streamingAnswer,
    isLoading,
    isStreaming:
      isStreaming || (!!streamingAnswer && !!pendingRequestIdRef.current),
    isError,
    input: state.input,
    toolCallLogs,
    tools: state.tools,
    citationGenerationStarted,
    reportType: state.reportType,
    sessionTitle: state.sessionTitle,
    asyncStatuses,
    isArtifactReportSchedulingMessageDisabled:
      state.isArtifactReportSchedulingMessageDisabled,

    disableArtifactReportSchedulingMessage,
    markReportScheduled,
    setIsArtifactReportSchedulingMessageDisabled:
      setters.setIsArtifactReportSchedulingMessageDisabled,
    sendMessage: actions.sendMessage,
    incrementVoiceCount,
    updateInput: actions.updateInput,
    clearConversation: actions.clearConversation,
    handleChoiceSelect: actions.handleChoiceSelect,
    setSessionId: actions.setSessionId,
    setMessages: actions.setMessages,
    setTools: actions.setTools,
    setReportType: actions.setReportType,
    setSessionTitle: setters.setSessionTitle,
    initializeToolsFromSession,
    stopRequest: actions.stopRequest,
    initializeChatState,
    hideMessage: actions.hideMessage,
  };

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
};
